import { Auth } from '@aws-amplify/auth';
import { Cache } from '@aws-amplify/cache';

/**
 * Per-stage AWS T&C Supergraph GraphQL endpoints (user route, Gandalf Bearer
 * auth). Unlike the Grimsby backend APIs (SigV4 via handleGrimsbyApiRequest),
 * the Supergraph authorizer validates the raw Gandalf idToken directly, so no
 * request signing is needed — just the Authorization header.
 *
 * Note: on localhost the beta Supergraph allows any localhost origin and skips
 * the clientId-per-origin check, so local dev works without registering the
 * Grimsby origins. Deployed stages require the Grimsby domains + app client
 * ids to be registered in AwsTcPlatformSupergraph frontendClients.ts first.
 */
const SUPERGRAPH_ENDPOINTS: Record<string, string> = {
    sam: 'https://beta.api.onboard.skillbuilder.training.aws.dev/graphql',
    beta: 'https://beta.api.onboard.skillbuilder.training.aws.dev/graphql',
    gamma: 'https://gamma.api.onboard.skillbuilder.training.aws.dev/graphql',
    prod: 'https://api.onboard.skillbuilder.aws/graphql',
};

/** A single GraphQL error entry as returned by the Supergraph. */
export interface SupergraphError {
    message: string;
    extensions?: { code?: string };
}

/** Error thrown when the Supergraph responds with GraphQL or HTTP errors. */
export class SupergraphRequestError extends Error {
    readonly status: number;
    readonly errors: SupergraphError[];

    constructor(message: string, status: number, errors: SupergraphError[]) {
        super(message);
        this.name = 'SupergraphRequestError';
        this.status = status;
        this.errors = errors;
    }
}

const getSupergraphEndpoint = (): string => {
    const stage = (
        window.__CONFIGURATION__?.STAGE ?? 'prod'
    ).toLowerCase() as keyof typeof SUPERGRAPH_ENDPOINTS;
    return SUPERGRAPH_ENDPOINTS[stage] ?? SUPERGRAPH_ENDPOINTS.prod;
};

/** Options for {@link handleSupergraphRequest}. */
export interface SupergraphRequestOptions {
    /**
     * When true, a response with BOTH data and errors (federation partial
     * success — e.g. one subgraph denied a field while others resolved)
     * returns the partial data instead of throwing. Responses with no data
     * still throw.
     */
    allowPartial?: boolean;
}

/**
 * Perform a GraphQL request against the AWS T&C Supergraph using the signed-in
 * user's Gandalf idToken (`Bearer` scheme). GraphQL-level errors arrive as
 * HTTP 200 with an `errors` array; both layers are surfaced as
 * {@link SupergraphRequestError} (unless `allowPartial` accepts partial data).
 *
 * @param query - GraphQL document (query or mutation)
 * @param variables - GraphQL variables object
 * @param options - See {@link SupergraphRequestOptions}
 * @return: A promise resolving with the `data` payload typed as `T`
 */
const handleSupergraphRequest = async <T>(
    query: string,
    variables: Record<string, unknown> = {},
    options: SupergraphRequestOptions = {},
): Promise<T> => {
    // Fetch a FRESH idToken from Amplify (auto-refreshes via the refresh
    // token when expired) rather than trusting the cached copy. An expired
    // token makes the Supergraph authorizer reject the request with a
    // response that lacks CORS headers, which the browser misreports as a
    // CORS failure. Fall back to the cache for resilience (and tests).
    let token: string | undefined;
    try {
        token = (await Auth.currentSession()).getIdToken().getJwtToken();
    } catch {
        token = Cache.getItem('federatedInfo')?.token;
    }
    if (!token) {
        throw new SupergraphRequestError(
            'No Gandalf idToken available — sign in first.',
            401,
            [],
        );
    }

    const response = await fetch(getSupergraphEndpoint(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query, variables }),
    });

    let result: { data?: T; errors?: SupergraphError[] };
    try {
        result = await response.json();
    } catch {
        throw new SupergraphRequestError(
            `Supergraph returned non-JSON response (HTTP ${response.status})`,
            response.status,
            [],
        );
    }

    // Federation partial success: data alongside errors. When permitted,
    // return what resolved (callers render per-field) instead of throwing.
    if (
        options.allowPartial &&
        response.ok &&
        result.data &&
        result.errors?.length
    ) {
        // eslint-disable-next-line no-console
        console.warn(
            '[supergraph] partial response accepted',
            result.errors.map((e) => e.message),
        );
        return result.data;
    }

    if (!response.ok || result.errors?.length) {
        const errors = result.errors ?? [];
        throw new SupergraphRequestError(
            errors[0]?.message ??
                `Supergraph request failed (HTTP ${response.status})`,
            response.status,
            errors,
        );
    }

    // A 200 with neither data nor errors (e.g. an empty `{}` body) would
    // otherwise return undefined and make callers throw a TypeError when they
    // dereference the payload. Surface it as a SupergraphRequestError so the
    // fail-open callers catch it like any other Supergraph failure.
    if (result.data == null) {
        throw new SupergraphRequestError(
            'Supergraph response contained no data',
            response.status,
            result.errors ?? [],
        );
    }

    return result.data;
};

export default handleSupergraphRequest;
